import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useAuth } from '@/providers/AuthProvider';
import { FloatingMessageNotificationToast, FloatingMessageNotification } from './floating-message-notification';
import { FloatingChatSystem } from './floating-chat-system';
import { db } from '@/lib/firebase';
import { collection, query, where, onSnapshot, orderBy, doc, getDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';

interface FloatingMessageContextType {
  notifications: FloatingMessageNotification[];
  openChat: (senderId: string) => void;
  closeNotification: (id: string) => void;
  markAsRead: (id: string) => void;
}

const FloatingMessageContext = createContext<FloatingMessageContextType | null>(null);

export function useFloatingMessages() {
  const context = useContext(FloatingMessageContext);
  if (!context) {
    throw new Error('useFloatingMessages must be used within FloatingMessageProvider');
  }
  return context;
}

interface FloatingMessageProviderProps {
  children: React.ReactNode;
}

export function FloatingMessageProvider({ children }: FloatingMessageProviderProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<FloatingMessageNotification[]>([]);
  const processedMessageIdsRef = useRef<Set<string>>(new Set());
  const initialSnapshotHandledRef = useRef(false);

  // Real-time message listening with Firebase
  useEffect(() => {
    if (!user?.uid) return;

    processedMessageIdsRef.current = new Set();
    initialSnapshotHandledRef.current = false;
    
    try {
      // Listen for new messages in conversations where user is a participant
      const messagesRef = collection(db, 'messages');
      const messagesQuery = query(
        messagesRef,
        where('participants', 'array-contains', user.uid),
        orderBy('createdAt', 'desc')
      );

      const unsubscribe = onSnapshot(messagesQuery, async (snapshot) => {
        if (!initialSnapshotHandledRef.current) {
          snapshot.docs.forEach((docSnapshot) => {
            processedMessageIdsRef.current.add(docSnapshot.id);
          });
          initialSnapshotHandledRef.current = true;
          return;
        }

        for (const change of snapshot.docChanges()) {
          if (change.type === 'added') {
            const data = change.doc.data();
            const messageId = change.doc.id;

            // Skip if we've already processed this message
            if (processedMessageIdsRef.current.has(messageId)) {
              continue;
            }

            // Mark as processed
            processedMessageIdsRef.current.add(messageId);

            // Only show notifications for messages from others
            if (data.senderId === user.uid) {
              continue;
            }

            // Additional safety check - make sure participants array doesn't only contain current user
            if (data.participants && Array.isArray(data.participants) && data.participants.length === 1 && data.participants[0] === user.uid) {
              continue;
            }

            // Check if this is a recent message (within last 2 minutes)
            const messageTimestamp = data.createdAt?.toDate?.() || new Date();
            const timeSinceMessage = Date.now() - messageTimestamp.getTime();
            
            if (timeSinceMessage < 120000) { // 2 minutes
              // Get sender info
              let senderName = data.senderName || 'Someone';
              let senderPhoto = data.senderPhoto;
              
              try {
                const senderDoc = await getDoc(doc(db, 'users', data.senderId));
                if (senderDoc.exists()) {
                  const senderData = senderDoc.data();
                  senderName = senderData.name || senderData.displayName || senderName;
                  senderPhoto = senderData.photoURL || senderPhoto;
                }
              } catch (error) {
                console.warn('Could not fetch sender info:', error);
              }

              const notification: FloatingMessageNotification = {
                id: `${messageId}-${Date.now()}`,
                messageId,
                senderId: data.senderId,
                senderName,
                senderPhoto,
                content: data.content || 'Sent you a message',
                timestamp: messageTimestamp,
                seen: data.seen || false,
                conversationId: data.conversationId || data.senderId
              };

              setNotifications(prev => {
                // Don't add duplicate notifications
                const exists = prev.some(n => n.messageId === messageId);
                if (exists) return prev;
                
                // Keep only the 5 most recent notifications
                return [notification, ...prev].slice(0, 5);
              });
            }
          }
        }
      }, (error) => {
        const errorCode = String((error as any)?.code || '').toLowerCase();
        if (!errorCode.includes('permission-denied')) {
          console.error('Error listening to messages:', error);
        }
      });

      return () => {
        unsubscribe();
      };
    } catch (error) {
      console.error('Error setting up message listener:', error);
    }
  }, [user?.uid]);

  const openChat = (senderId: string) => {
    const targetSenderId = typeof senderId === 'string' ? senderId.trim() : '';
    if (!targetSenderId) return;

    setNotifications((prev) => prev.filter((notification) => notification.senderId !== targetSenderId));
    navigate(`/messages?user=${encodeURIComponent(targetSenderId)}`);
  };

  const closeNotification = (id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const markAsRead = (id: string) => {
    setNotifications(prev => 
      prev.map(n => n.id === id ? { ...n, seen: true } : n)
    );
  };

  const contextValue: FloatingMessageContextType = {
    notifications,
    openChat,
    closeNotification,
    markAsRead
  };

  return (
    <FloatingMessageContext.Provider value={contextValue}>
      {children}
      
      {/* Render floating chat system */}
      <FloatingChatSystem />
      
      {/* Render notification toasts */}
      <div className="fixed bottom-4 right-4 z-[9999] space-y-2">
        {notifications.map((notification, index) => (
          <FloatingMessageNotificationToast
            key={notification.id}
            notification={notification}
            position={index}
            onClose={() => closeNotification(notification.id)}
            onReply={() => openChat(notification.senderId)}
            onMarkAsRead={() => markAsRead(notification.id)}
            onOpenChat={() => openChat(notification.senderId)}
          />
        ))}
      </div>
    </FloatingMessageContext.Provider>
  );
}